import { motion } from 'framer-motion';
import { 
  Wifi,
  Server,
  Bell,
  Database,
  Globe,
  Save,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MQTTServerList, LNSServerList } from '@/components/settings/ServerList';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function Settings() {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6 max-w-4xl"
    >
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground">
          Configuración
        </h1>
        <p className="text-muted-foreground mt-1">
          Ajusta los parámetros del sistema
        </p>
      </div>

      {/* LNS Servers (ChirpStack) - Primary for LoRaWAN devices */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-display">
              <Server className="w-5 h-5 text-primary" />
              Servidores LNS (LoRaWAN)
            </CardTitle>
            <CardDescription>
              Gestiona los servidores ChirpStack para comunicación LoRaWAN con controladores y sensores
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LNSServerList />
          </CardContent>
        </Card>
      </motion.div>

      {/* MQTT Servers */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-display">
              <Wifi className="w-5 h-5 text-primary" />
              Servidores MQTT
            </CardTitle>
            <CardDescription>
              Gestiona los brokers MQTT para comunicación directa con dispositivos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MQTTServerList />
          </CardContent>
        </Card>
      </motion.div>

      {/* AEMET Configuration */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-display">
              <Globe className="w-5 h-5 text-primary" />
              API AEMET
            </CardTitle>
            <CardDescription>
              Configura el acceso a los datos meteorológicos de AEMET
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="aemet-key">API Key</Label>
              <Input 
                id="aemet-key" 
                type="password" 
                placeholder="Tu API Key de AEMET" 
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                La API Key de AEMET se configura en el backend. 
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline" size="sm" asChild>
                <a 
                  href="https://opendata.aemet.es" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Obtener API Key
                </a>
              </Button>
              <span className="text-xs text-muted-foreground">
                Regístrate en AEMET OpenData
              </span>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Notifications */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-display">
              <Bell className="w-5 h-5 text-primary" />
              Notificaciones
            </CardTitle>
            <CardDescription>
              Configura cuándo y cómo recibir alertas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dispositivo offline</p>
                <p className="text-sm text-muted-foreground">
                  Notificar cuando un dispositivo pierde conexión
                </p>
              </div>
              <Switch defaultChecked />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Batería baja</p>
                <p className="text-sm text-muted-foreground">
                  Notificar cuando la batería baja del 20%
                </p>
              </div>
              <Switch defaultChecked />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Triggers activados</p>
                <p className="text-sm text-muted-foreground">
                  Notificar cuando se activa un trigger
                </p>
              </div>
              <Switch defaultChecked />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Riego completado</p>
                <p className="text-sm text-muted-foreground">
                  Notificar al completar una programación de riego
                </p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* System */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-display">
              <Database className="w-5 h-5 text-primary" />
              Sistema
            </CardTitle>
            <CardDescription>
              Configuración general del sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Zona horaria</Label>
                <Select defaultValue="europe-madrid">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="europe-madrid">Europe/Madrid</SelectItem>
                    <SelectItem value="europe-london">Europe/London</SelectItem>
                    <SelectItem value="utc">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Idioma</Label>
                <Select defaultValue="es">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Intervalo de polling (segundos)</Label>
              <Input type="number" defaultValue="60" />
              <p className="text-xs text-muted-foreground">
                Frecuencia de actualización de datos desde los dispositivos
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Save button */}
      <motion.div variants={itemVariants} className="flex justify-end">
        <Button className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow gap-2">
          <Save className="w-4 h-4" />
          Guardar cambios
        </Button>
      </motion.div>
    </motion.div>
  );
}
